import { useState } from 'react'
import type { ClinicalEventType, ClinicalEventCreate } from '@rondela/shared'
import { Button } from '../../../shared/components/Button'

interface ClinicalEventFormProps {
  onSubmit: (data: ClinicalEventCreate) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

const eventTypes: { value: ClinicalEventType; label: string }[] = [
  { value: 'consulta', label: 'Consulta' },
  { value: 'emergencia', label: 'Emergencia' },
  { value: 'cirugia', label: 'Cirugía' },
  { value: 'hospitalizacion', label: 'Hospitalización' },
  { value: 'procedimiento', label: 'Procedimiento' },
  { value: 'diagnostico', label: 'Diagnóstico' },
  { value: 'tratamiento', label: 'Tratamiento' },
  { value: 'otro', label: 'Otro' },
]

export function ClinicalEventForm({ onSubmit, onCancel, isLoading }: ClinicalEventFormProps) {
  const [tipo, setTipo] = useState<ClinicalEventType>('consulta')
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0])
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [veterinario, setVeterinario] = useState('')
  const [clinica, setClinica] = useState('')
  const [diagnostico, setDiagnostico] = useState('')
  const [tratamiento, setTratamiento] = useState('')
  const [peso, setPeso] = useState('')
  const [notas, setNotas] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const data = {
      tipo,
      fecha: fecha || new Date().toISOString().split('T')[0],
      titulo: titulo || '',
      descripcion: descripcion || undefined,
      veterinario: veterinario || undefined,
      clinica: clinica || undefined,
      diagnostico: diagnostico || undefined,
      tratamiento: tratamiento || undefined,
      peso: peso ? parseFloat(peso) : undefined,
      notas: notas || undefined,
    } as ClinicalEventCreate

    await onSubmit(data)
  }

  return (
    <form onSubmit={handleSubmit} className="clinical-event-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="tipo">Tipo de evento *</label>
          <select
            id="tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as ClinicalEventType)}
            required
          >
            {eventTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="fecha">Fecha *</label>
          <input
            type="date"
            id="fecha"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="titulo">Título *</label>
        <input
          type="text"
          id="titulo"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej: Control de rutina, Vacunación anual..."
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="descripcion">Descripción</label>
        <textarea
          id="descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Describe el evento..."
          rows={3}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="veterinario">Veterinario</label>
          <input
            type="text"
            id="veterinario"
            value={veterinario}
            onChange={(e) => setVeterinario(e.target.value)}
            placeholder="Nombre del veterinario"
          />
        </div>
        <div className="form-group">
          <label htmlFor="clinica">Clínica</label>
          <input
            type="text"
            id="clinica"
            value={clinica}
            onChange={(e) => setClinica(e.target.value)}
            placeholder="Nombre de la clínica"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="diagnostico">Diagnóstico</label>
        <textarea
          id="diagnostico"
          value={diagnostico}
          onChange={(e) => setDiagnostico(e.target.value)}
          placeholder="Diagnóstico médico..."
          rows={2}
        />
      </div>

      <div className="form-group">
        <label htmlFor="tratamiento">Tratamiento</label>
        <textarea
          id="tratamiento"
          value={tratamiento}
          onChange={(e) => setTratamiento(e.target.value)}
          placeholder="Tratamiento indicado..."
          rows={2}
        />
      </div>

      <div className="form-group">
        <label htmlFor="peso">Peso (kg)</label>
        <input
          type="number"
          id="peso"
          value={peso}
          onChange={(e) => setPeso(e.target.value)}
          placeholder="Peso actual"
          step="0.1"
          min="0"
        />
        <span className="form-hint">Si ingresas el peso, se actualizará el peso actual de la mascota</span>
      </div>

      <div className="form-group">
        <label htmlFor="notas">Notas adicionales</label>
        <textarea
          id="notas"
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          placeholder="Notas adicionales..."
          rows={2}
        />
      </div>

      <div className="form-actions">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading || !titulo}>
          {isLoading ? 'Guardando...' : 'Guardar evento'}
        </Button>
      </div>
    </form>
  )
}
